/*
 * Portfolio project data.
 *
 * visibility:
 *   "public"   - open source, repo link is shown alongside the live site
 *   "private"  - client repo stays closed, only the live product is linked
 *   "internal" - internal/confidential delivery, described only, no links at all
 *
 * clientKey doubles as the Isotope filter class, so keep it a single word.
 */

const GITHUB_USER = "https://github.com/DennekJr";

export const clients = [
  { key: "*", label: "All" },
  { key: "livelabs", label: "LiveLabs" },
  { key: "carterbiggs", label: "Carter Biggs" },
  { key: "client", label: "Client Work" },
  { key: "personal", label: "Personal" },
];

const projects = [
  /* ---------------- LiveLabs (formerly LiveCapital) ---------------- */
  {
    id: "live-capital",
    title: "LiveCapital",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "private",
    image: require("../../image/portfolio/live-capital.png"),
    live: "https://www.livelabs.africa/",
    summary:
      "Investment platform front end. The company has since rebranded, so the original livecapital.fund domain now resolves to LiveLabs.",
    tech: ["React", "TypeScript"],
  },
  {
    id: "lc-staff-review",
    title: "Staff Performance Review",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "public",
    image: require("../../image/portfolio/lc-staff-review.png"),
    live: "https://live-capital-staff-review.vercel.app",
    repo: `${GITHUB_USER}/Live-Capital-Staff-Review`,
    summary:
      "Peer review portal where every staff member scores every colleague across 12 metrics. Reviews stay anonymous to peers while admins retain reviewer identity for integrity, with an analytics dashboard behind restricted access.",
    tech: ["Next.js", "JavaScript"],
  },
  {
    id: "engineer-monitor",
    title: "Engineering Monitor",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "internal",
    image: require("../../image/portfolio/engineer-monitor.png"),
    summary:
      "Internal mobile app for monitoring every engineer across the company. Built for internal use only, so there is no public repository or live link.",
    tech: ["React Native"],
  },
  {
    id: "partyverse",
    title: "PartyVerse",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "private",
    image: require("../../image/portfolio/partyverse.png"),
    live: "https://www.partyverse.com/",
    summary:
      "Nigeria's all-in-one celebration platform: plan events, sell tickets, send gifts, discover experiences and keep the memories in one place, with the web product driving installs of the companion iOS and Android apps.",
    tech: ["React", "TypeScript"],
  },
  {
    id: "drivewell",
    title: "DriveWell",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "private",
    image: require("../../image/portfolio/drivewell.png"),
    live: "https://www.drivewellapp.com/",
    summary: "Driver safety and vehicle management product.",
    tech: ["React", "TypeScript"],
  },
  {
    id: "drivewell-assistant",
    title: "DriveWell Assistant",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "public",
    image: require("../../image/portfolio/drivewell-assistant.png"),
    live: "https://drivewell-assistant.vercel.app",
    repo: `${GITHUB_USER}/drivewell-assistant`,
    summary:
      "Fulfillment operations dashboard for DriveWell: orders board, client and supplier records, pricing comparison, invoicing and a daily briefing that surfaces overdue invoices and stalled orders.",
    tech: ["React", "TypeScript", "Vite"],
  },
  {
    id: "cast",
    title: "CAST",
    client: "LiveLabs",
    clientKey: "livelabs",
    visibility: "private",
    image: require("../../image/portfolio/cast.png"),
    summary:
      "Creative talent platform for CAST. The cast.africa domain no longer resolves, so the tile is kept as a record of the work without a live link.",
    tech: ["React"],
  },

  /* ---------------- Carter Biggs ---------------- */
  {
    id: "edutrace",
    title: "EduTrace",
    client: "Carter Biggs",
    clientKey: "carterbiggs",
    visibility: "internal",
    image: require("../../image/portfolio/edutrace.png"),
    role: "Product engineer and technical lead - full stack, requirement to deploy",
    summary:
      "Track-and-trace and governance platform for federal book distribution, following teaching and learning materials from supplier packing through warehouses and 3PL to the school that signs for them. I built both sides: a NestJS and Prisma core over a 43-model PostgreSQL domain, a separate scan service, notification and media microservices, and the Next.js console. Under client confidentiality, so no public repository or live link.",
    highlights: [
      "Governance engine for the federal-state-SUBEB delegation chain, role-scoped across federal, state, warehouse, 3PL, supplier and agent users",
      "Chain of custody on QR consignments, two-stage OTP handover, GPS waypoints and photo-verified delivery, posting inventory on acceptance",
      "M&E suite: distribution KPIs, an Annex 6B scorecard under maker-checker, per-school delivered stock and field issue reporting",
      "Read-only auditor role for World Bank and M&E oversight - full visibility, no write path",
      "Integrity safeguards: cross-state flagging, packing lists editable only until packed, change tracking and explicit lifecycle status",
      "Device management for field hardware - enrollment, heartbeats, policy push, remote commands and compliance",
      "Queue-backed notifications on Bull and Redis for email, push and PDF reporting; staged CI/CD on GitHub Actions",
    ],
    tech: [
      "NestJS",
      "Prisma",
      "PostgreSQL",
      "Next.js",
      "TypeScript",
      "RTK Query",
      "Tailwind",
      "MongoDB",
      "Redis",
      "GitHub Actions",
    ],
  },

  /* ---------------- Independent client work ---------------- */
  {
    id: "ahumma",
    title: "Ahumma",
    client: "Ahumma",
    clientKey: "client",
    visibility: "public",
    image: require("../../image/portfolio/ahumma.png"),
    live: "https://ahumma-storefront.vercel.app",
    repo: `${GITHUB_USER}/ahumma-storefront`,
    summary:
      "Editorial commerce storefront for a Lagos body-care brand. Next.js on the Frontdesk Storefront API, with server-side checkout, delivery zones and signed webhooks.",
    tech: ["Next.js", "TypeScript", "Frontdesk API"],
  },
  {
    id: "secret-passion",
    title: "Secret'Passion",
    client: "Secret'Passion",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/secret-passion.png"),
    live: "https://secret-passion-inky.vercel.app/",
    summary:
      "French-language storefront for a Nigeria-registered hair-care label built around textured hair: shampoos, conditioners, detanglers, mists and growth oils sold as singles and gift sets. Prices settle in naira while the free-delivery threshold is quoted in euros for the French market, alongside customer results and full CGV and legal pages.",
    tech: ["Next.js", "TypeScript"],
  },
  {
    id: "lovedot",
    title: "LOVE DOT",
    client: "LOVE DOT, Inc.",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/lovedot.png"),
    live: "https://lovedot.co/",
    summary:
      "Shopify storefront for a design house working in West African textiles - Adire, Kente, Akwete and Batik sourced across Ghana and Nigeria - selling small-batch menswear and womenswear into the US and Canada. I handled debugging, feature implementation and store setup, and reviewed the storefront for overall brand efficiency.",
    tech: ["Shopify", "Liquid"],
  },
  {
    id: "vistosa",
    title: "Vistosa",
    client: "Vistosa Nigeria",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/vistosa.png"),
    live: "https://vistosa.vercel.app",
    summary:
      "Fashion and streetwear storefront for a Nigerian label. Category browsing across tops, outwear, bottoms, dresses and accessories, with search, cart and a sizing guide.",
    tech: ["Next.js", "TypeScript"],
  },
  {
    id: "cre8",
    title: "CRE8",
    client: "CRE8.XYZ",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/cre8.png"),
    live: "https://www.cre8.xyz/",
    summary:
      "Front end for CRE8, a peer-owned network where people and autonomous agents build worlds, media, markets and software on one shared graph, with every meaningful action signed, replayed and proven.",
    tech: ["React", "TypeScript"],
  },
  {
    id: "true-profit",
    title: "TrueProfit",
    client: "TrueProfit",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/true-profit.png"),
    live: "https://usetrueprofit.com/",
    summary:
      "Profitability analytics for businesses: revenue against real costs so owners see true margin rather than turnover. Google and email sign-in behind an authenticated account, priced and reported in naira.",
    tech: ["TypeScript"],
  },
  {
    id: "asf",
    title: "Africa Skincare Festival",
    client: "Africa Skincare Festival",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/asf.png"),
    live: "https://africaskincarefestival.com/",
    summary: "Festival site covering programme, speakers and ticketing.",
    tech: ["React"],
  },
  {
    id: "nollytime",
    title: "NollyTime",
    client: "NollyTime",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/nollytime.png"),
    live: "https://www.nollytime.com/",
    summary: "Nollywood streaming and film discovery platform.",
    tech: ["React", "TypeScript"],
  },
  {
    id: "zenthly",
    title: "Zenthly",
    client: "Zenthly",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/zenthly.png"),
    live: "https://www.zenthly.com/",
    summary: "Product marketing site and customer-facing web app.",
    tech: ["Next.js", "TypeScript"],
  },
  {
    id: "legendary-xmas",
    title: "Legendary Xmas Concert",
    client: "Legendary Xmas Concert",
    clientKey: "client",
    visibility: "private",
    image: require("../../image/portfolio/legendaryxmasconcert.png"),
    live: "https://legendaryxmasconcert.com/",
    summary: "Concert landing page with line-up and ticket registration.",
    tech: ["React"],
  },

  /* ---------------- Personal ---------------- */
  {
    id: "just-for-quacks",
    title: "Just For Quacks",
    client: "Personal project",
    clientKey: "personal",
    visibility: "private",
    image: require("../../image/portfolio/just-for-quacks.png"),
    live: "https://justforquacks-web-eta.vercel.app/",
    summary:
      "Live comedy website for an Abuja comedy club, featuring shows, performer sign-ups, stage clips, comedian profiles and venue information.",
    tech: [],
  },
  {
    id: "premium-pantry",
    title: "Premium Pantry",
    client: "Personal project",
    clientKey: "personal",
    visibility: "private",
    image: require("../../image/portfolio/premium-pantry.png"),
    live: "https://premium-pantry-web.vercel.app/",
    summary:
      "Nigerian snack storefront with single packs, occasion boxes and monthly subscription plans, linking shoppers to store checkout, Paystack subscriptions and WhatsApp ordering.",
    tech: [],
  },
  {
    id: "wedding",
    title: "Wedding",
    client: "Personal project",
    clientKey: "personal",
    visibility: "public",
    image: require("../../image/portfolio/wedding.png"),
    live: "https://wedding-ochre.vercel.app/",
    repo: `${GITHUB_USER}/wedding`,
    summary: "Wedding invitation and RSVP site.",
    tech: ["React", "CSS"],
  },
];

export default projects;
